import { ListChecks, Check, Square, CheckSquare } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { Checklist } from '../../../shared/types/chat'

interface Props {
  checklist: Checklist
  onToggle?: (id: string) => void
  onDone?: () => void
}

/**
 * Interactive checklist -- AI creates it, user checks items off.
 * Checking items is local UI state. The user can send a chat message
 * to request changes.
 */
export default function ChecklistCard({ checklist, onToggle, onDone }: Props) {
  const { t } = useTranslation('chat')
  const checkedCount = checklist.items.filter(i => i.checked).length
  const total = checklist.items.length
  const allChecked = checkedCount === total

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`glass-card rounded-xl overflow-hidden border-l-4 ${
        allChecked ? 'border-emerald bg-emerald/5' : 'border-gold bg-gold/5'
      }`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListChecks size={14} className="text-gold" />
            <span className="text-xs font-semibold uppercase tracking-wider text-gold">
              {checklist.label || t('checklist.defaultLabel')}
            </span>
          </div>
          <span className={`text-xs ${allChecked ? 'text-emerald font-semibold' : 'text-pearl/60'}`}>
            {checkedCount}/{total}
          </span>
        </div>

        <h4 className="text-ivory font-medium text-sm mb-1">{checklist.title}</h4>
        {checklist.subtitle && (
          <p className="text-pearl/50 text-xs mb-3">{checklist.subtitle}</p>
        )}

        {/* Items */}
        <div className="space-y-1">
          {checklist.items.map((item) => (
            <motion.button
              key={item.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => onToggle?.(item.id)}
              className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/[0.03] transition-colors text-left"
            >
              <div className="shrink-0 mt-0.5">
                {item.checked ? (
                  <CheckSquare size={16} className="text-emerald" />
                ) : (
                  <Square size={16} className="text-pearl/55" />
                )}
              </div>
              <div>
                <span className={`text-xs ${
                  item.checked ? 'text-pearl/60 line-through' : 'text-ivory'
                }`}>
                  {item.label}
                </span>
                {item.detail && (
                  <p className="text-xs text-pearl/50 mt-0.5">{item.detail}</p>
                )}
              </div>
            </motion.button>
          ))}
        </div>

        {/* Note */}
        {checklist.note && (
          <div className="p-2.5 rounded-lg bg-gold/5 border border-gold/10 mt-3">
            <p className="text-xs text-pearl/70 italic">"{checklist.note}"</p>
          </div>
        )}

        {/* Done button */}
        {allChecked && !checklist.submitted && (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => onDone?.()}
            className="w-full mt-3 py-2.5 px-4 rounded-full bg-gold text-midnight text-sm font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2"
          >
            <Check size={14} />
            {t('checklist.allDone')}
          </motion.button>
        )}

        {checklist.submitted && (
          <div className="mt-3 pt-2 border-t border-white/5 text-center">
            <span className="text-xs text-emerald/60">{t('checklist.completed')}</span>
          </div>
        )}
      </div>
    </motion.div>
  )
}
